/**
 * Dynamo design layer behaviour.
 * - Quick add to bag (talks to the theme cart drawer through Shopify standard events)
 * - Scroll reveal
 * - Swipe rails with arrow buttons
 * - Tabs
 * - Scent finder quiz
 *
 * Every feature is progressive: without JS, forms post to /cart/add and rails scroll natively.
 */

const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* ------------------------------------------------------------------ */
/* Quick add                                                           */
/* ------------------------------------------------------------------ */

let eventsModule = null;
async function loadEvents() {
  if (eventsModule !== null) return eventsModule;
  try {
    eventsModule = await import('@shopify/events');
  } catch (error) {
    eventsModule = false;
  }
  return eventsModule;
}

function cartSectionIds() {
  return Array.from(document.querySelectorAll('cart-items-component'))
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
}

async function fetchCart() {
  const root = window.Theme?.routes?.cart_url || '/cart';
  const response = await fetch(`${root}.json`, { headers: { Accept: 'application/json' }, credentials: 'same-origin' });
  if (!response.ok) throw new Error(`Cart fetch failed: ${response.status}`);
  return response.json();
}

async function quickAdd(form) {
  const button = form.querySelector('button[type="submit"]');
  const label = form.querySelector('[data-dy-add-label]');
  const original = label ? label.textContent : '';
  const variantId = String(new FormData(form).get('id') || '');
  const quantity = Number(new FormData(form).get('quantity') || 1);

  button?.classList.add('is-loading');
  button?.setAttribute('aria-busy', 'true');

  const events = await loadEvents();
  const sections = cartSectionIds();
  const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add';

  let deferred = null;
  if (events && events.CartLinesUpdateEvent) {
    deferred = events.CartLinesUpdateEvent.createPromise();
    form.dispatchEvent(
      new events.CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );
  }

  try {
    const response = await fetch(`${addUrl}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({
        items: [{ id: Number(variantId), quantity }],
        sections: sections.join(','),
        sections_url: window.location.pathname,
      }),
    });
    const data = await response.json();

    if (!response.ok || data.status) {
      throw new Error(data.description || data.message || 'Could not add to bag');
    }

    const cart = await fetchCart();

    if (deferred && events) {
      deferred.resolve({
        cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'dy-quick-add',
          sourceId: variantId,
          itemCount: quantity,
          sections: data.sections,
          didError: false,
        },
      });
    } else {
      // No event bridge available: fall back to the cart page.
      window.location.href = window.Theme?.routes?.cart_url || '/cart';
      return;
    }

    button?.classList.add('is-added');
    if (label) label.textContent = 'Added';
    setTimeout(() => {
      button?.classList.remove('is-added');
      if (label) label.textContent = original;
    }, 2200);
  } catch (error) {
    if (deferred && events) {
      // Mirror product-form.js: resolve with didError so cart listeners stay in sync.
      fetchCart()
        .then((cart) =>
          deferred.resolve({
            cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
            detail: { items: cart.items, source: 'dy-quick-add', sourceId: variantId, itemCount: quantity, didError: true },
          })
        )
        .catch(deferred.reject);
    }
    if (label) label.textContent = error.message.length < 40 ? error.message : 'Try again';
    setTimeout(() => {
      if (label) label.textContent = original;
    }, 3000);
  } finally {
    button?.classList.remove('is-loading');
    button?.removeAttribute('aria-busy');
  }
}

document.addEventListener('submit', (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.hasAttribute('data-dy-add')) return;
  event.preventDefault();
  quickAdd(form);
});

/* ------------------------------------------------------------------ */
/* Scroll reveal                                                       */
/* ------------------------------------------------------------------ */

const revealObserver =
  'IntersectionObserver' in window && !reduceMotion
    ? new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            entry.target.classList.add('is-visible');
            revealObserver.unobserve(entry.target);
          }
        },
        { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
      )
    : null;

function initReveal(root = document) {
  root.querySelectorAll('[data-dy-reveal]:not(.is-visible)').forEach((el) => {
    if (revealObserver) revealObserver.observe(el);
    else el.classList.add('is-visible');
  });
}

/* ------------------------------------------------------------------ */
/* Rails                                                               */
/* ------------------------------------------------------------------ */

function initRails(root = document) {
  root.querySelectorAll('[data-dy-rail]').forEach((wrap) => {
    if (wrap.dataset.dyReady) return;
    wrap.dataset.dyReady = 'true';

    const track = wrap.querySelector('[data-dy-track]');
    const prev = wrap.querySelector('[data-dy-prev]');
    const next = wrap.querySelector('[data-dy-next]');
    if (!track) return;

    const step = () => {
      const item = track.firstElementChild;
      if (!item) return track.clientWidth;
      const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    };

    const update = () => {
      const max = track.scrollWidth - track.clientWidth - 2;
      if (prev) prev.disabled = track.scrollLeft <= 2;
      if (next) next.disabled = track.scrollLeft >= max;
      const nav = wrap.querySelector('.dy-rail-nav');
      if (nav) nav.hidden = max <= 0;
    };

    prev?.addEventListener('click', () => track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
    next?.addEventListener('click', () => track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' }));
    wrap.dyUpdate = update;
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  });
}

/* ------------------------------------------------------------------ */
/* Tabs                                                                */
/* ------------------------------------------------------------------ */

function initTabs(root = document) {
  root.querySelectorAll('[data-dy-tabs]').forEach((tabs) => {
    if (tabs.dataset.dyReady) return;
    tabs.dataset.dyReady = 'true';

    const buttons = Array.from(tabs.querySelectorAll('[role="tab"]'));
    const select = (button, focus = false) => {
      buttons.forEach((b) => {
        const selected = b === button;
        b.setAttribute('aria-selected', String(selected));
        b.tabIndex = selected ? 0 : -1;
        const panel = document.getElementById(b.getAttribute('aria-controls'));
        if (panel) {
          panel.hidden = !selected;
          if (selected) {
            initRails(panel);
            initReveal(panel);
            panel.querySelectorAll('[data-dy-rail]').forEach((rail) => rail.dyUpdate?.());
          }
        }
      });
      if (focus) button.focus();
    };

    buttons.forEach((button, index) => {
      button.addEventListener('click', () => select(button));
      button.addEventListener('keydown', (event) => {
        if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
        event.preventDefault();
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        select(buttons[(index + delta + buttons.length) % buttons.length], true);
      });
    });
  });
}

/* ------------------------------------------------------------------ */
/* Scent finder                                                        */
/* ------------------------------------------------------------------ */

function initFinder(root = document) {
  root.querySelectorAll('[data-dy-finder]').forEach((finder) => {
    if (finder.dataset.dyReady) return;
    finder.dataset.dyReady = 'true';

    const steps = Array.from(finder.querySelectorAll('[data-dy-step]'));
    const resultStep = finder.querySelector('[data-dy-result-step]');
    const results = Array.from(finder.querySelectorAll('[data-dy-result]'));
    const empty = finder.querySelector('[data-dy-empty]');
    const back = finder.querySelector('[data-dy-back]');
    const restart = finder.querySelector('[data-dy-restart]');
    const bars = Array.from(finder.querySelectorAll('[data-dy-progress] span'));
    const limit = Number(finder.dataset.limit || 6);
    const answers = {};
    let current = 0;

    const show = (index, focus = true) => {
      current = index;
      steps.forEach((step, i) => (step.hidden = i !== index));
      if (resultStep) resultStep.hidden = index !== steps.length;
      bars.forEach((bar, i) => bar.classList.toggle('is-on', i <= index));
      if (back) back.hidden = index === 0;
      if (restart) restart.hidden = index !== steps.length;
      const heading = index === steps.length ? resultStep?.querySelector('h3') : steps[index]?.querySelector('h3');
      if (focus) heading?.focus({ preventScroll: true });
    };

    const score = () => {
      const who = answers.for || 'any';
      const mood = answers.family || '';
      const ranked = results
        .map((el, order) => {
          const forValue = el.dataset.for || 'all';
          const families = (el.dataset.family || '').split(' ').filter(Boolean);
          const whoOk = who === 'any' || forValue === 'all' || forValue === who;
          const moodOk = !mood || families.includes(mood);
          const available = el.dataset.available === 'true';
          return { el, order, whoOk, moodOk, available };
        })
        .filter((r) => r.whoOk && r.moodOk)
        .sort((a, b) => Number(b.available) - Number(a.available) || a.order - b.order);

      results.forEach((el) => (el.hidden = true));
      ranked.slice(0, limit).forEach((r) => (r.el.hidden = false));
      if (empty) empty.hidden = ranked.length > 0;

      const summary = finder.querySelector('[data-dy-summary]');
      if (summary) {
        const parts = Object.keys(answers).map((key) => finder.querySelector(`[data-key="${key}"][data-value="${answers[key]}"] strong`)?.textContent?.trim());
        summary.textContent = parts.filter(Boolean).join(' · ');
      }
    };

    finder.querySelectorAll('[data-key]').forEach((option) => {
      option.addEventListener('click', () => {
        const { key, value } = option.dataset;
        answers[key] = value;
        option
          .closest('[data-dy-step]')
          ?.querySelectorAll('[data-key]')
          .forEach((o) => o.setAttribute('aria-pressed', String(o === option)));
        const next = current + 1;
        if (next >= steps.length) score();
        show(next);
      });
    });

    back?.addEventListener('click', () => show(Math.max(0, current - 1)));
    restart?.addEventListener('click', () => {
      Object.keys(answers).forEach((k) => delete answers[k]);
      finder.querySelectorAll('[aria-pressed="true"]').forEach((o) => o.setAttribute('aria-pressed', 'false'));
      show(0);
    });

    show(0, false);
  });
}

/* ------------------------------------------------------------------ */
/* Boot + theme editor support                                         */
/* ------------------------------------------------------------------ */

function init(root = document) {
  initReveal(root);
  initRails(root);
  initTabs(root);
  initFinder(root);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => init());
} else {
  init();
}

document.addEventListener('shopify:section:load', (event) => init(event.target));
